import {
  AddressSearchTabData,
  AddressSearchTabId,
  AddressSearchTabItem,
  AddressSearchTabSection,
} from '~/components/component-library/AddressSearchInput/types'
import { AddressSearchResult } from '~/types'

type BrowseTabId = Exclude<AddressSearchTabId, 'all'>

const TAB_LABELS: Record<AddressSearchTabId, string> = {
  all: 'All',
  recents: 'Recents',
  accounts: 'Accounts',
  banks: 'Banks',
  contacts: 'Contacts',
}

const BROWSE_TAB_ORDER: BrowseTabId[] = [
  'recents',
  'accounts',
  'banks',
  'contacts',
]

const browseRows = (
  tab: BrowseTabId,
  tabData: AddressSearchTabData,
): AddressSearchResult[] =>
  tab === 'recents' ? tabData.recents : tabData[tab].browse

const matchCount = (
  tab: AddressSearchTabId,
  tabData: AddressSearchTabData,
): number => {
  if (tab === 'all') {
    const { accounts, banks, contacts, external } = tabData.all
    return accounts.length + banks.length + contacts.length + external.length
  }
  // Recents is replaced by All while searching, so it never shows a count.
  if (tab === 'recents') return tabData.recents.length
  return tabData[tab].matching.length
}

// Visibility is driven by the browse datasets (not query matches) so the tab
// bar doesn't flicker as the user types. While searching, "All" replaces
// Recents as the unified results view and each label carries its match count.
export const getTabItems = ({
  tabData,
  hasQuery,
}: {
  tabData: AddressSearchTabData
  hasQuery: boolean
}): AddressSearchTabItem[] => {
  const browseTabs = BROWSE_TAB_ORDER.filter(
    (tab) => browseRows(tab, tabData).length > 0,
  )

  if (!hasQuery) {
    return browseTabs.map((id) => ({ id, label: TAB_LABELS[id] }))
  }

  const searchTabs: AddressSearchTabId[] = [
    'all',
    ...browseTabs.filter((tab) => tab !== 'recents'),
  ]
  return searchTabs.map((id) => ({
    id,
    label: `${TAB_LABELS[id]} (${matchCount(id, tabData)})`,
  }))
}

// The effective tab: the requested one while it's still visible, else the first
// visible tab (falling back to 'all' when nothing is visible).
export const resolveActiveTab = ({
  requestedTab,
  tabItems,
}: {
  requestedTab: AddressSearchTabId | undefined
  tabItems: AddressSearchTabItem[]
}): AddressSearchTabId =>
  (requestedTab && tabItems.some((tab) => tab.id === requestedTab)
    ? requestedTab
    : tabItems[0]?.id) ?? 'all'

// Next tab when cycling with Tab / Shift-Tab; wraps around. Undefined if empty.
export const cycleTab = ({
  activeTab,
  tabItems,
  reverse = false,
}: {
  activeTab: AddressSearchTabId
  tabItems: AddressSearchTabItem[]
  reverse?: boolean
}): AddressSearchTabId | undefined => {
  if (!tabItems.length) return undefined
  const index = tabItems.findIndex((tab) => tab.id === activeTab)
  const step = reverse ? -1 : 1
  return tabItems[(index + step + tabItems.length) % tabItems.length].id
}

export const buildTabSections = ({
  tab,
  tabData,
  hasQuery,
}: {
  tab: AddressSearchTabId
  tabData: AddressSearchTabData
  hasQuery: boolean
}): AddressSearchTabSection[] => {
  if (tab === 'all') {
    const { accounts, banks, contacts, external } = tabData.all
    return [
      { title: 'Accounts', rows: accounts },
      { title: 'Banks', rows: banks },
      { title: 'Contacts', rows: contacts },
      { title: 'External', rows: external },
    ].filter((section) => section.rows.length > 0)
  }

  if (tab === 'recents') {
    return tabData.recents.length
      ? [{ rows: tabData.recents, allowRemove: true }]
      : []
  }

  const rows = hasQuery ? tabData[tab].matching : tabData[tab].browse
  return rows.length ? [{ rows }] : []
}
